/* Crucial Tracking — the kit builder (October 2026 redesign, Concept A
   "the receipt", Kris 2026-10-15; simplified to the v2 redesign's receipt,
   Kris 2026-10-24). One step — pick your tracker — and "What you get",
   drawn as a receipt: the tracker line, the SIM line, the plan line (with
   the offer on, "First 12 months" and the plan's own price for them struck
   through beside an "Included" pill; Kris 2026-10-25) and ONE renewal
   sentence under it, the setup line, the delivery line and one total; under the button the guarantee and
   warranty on one line and a closed "Plan details" fold (the disclosure,
   the minimum total cost, the tax line, the tracker-only signpost). The
   plan is changed from a "Change plan" link that opens a compact <dialog>;
   the term is swapped by an inline link. Fleet mode (data-set="fleet") puts
   the quantity first; the other modes render no quantity control (`qty`
   stays in the state and the API). Enhances the markup that
   sections/ct-kit-builder.liquid renders; it never builds the builder.

   Rules enforced here:
   - ONE way to buy. Every "Add to cart" adds the paired kit: the
     tracker line plus the plan line (with its selling plan id), in one
     /cart/add. The plan line is sent first and the tracker last: the cart
     lists the newest line first, so the tracker sits above its plan in the
     cart and in the checkout's order summary. Nothing may read the
     tracker by its place in the request or in the answer — it is found by
     its variant. There is no purchase mode and no tracker-only add — that
     lives on the tracker's own page, which the signpost under the builder
     links to. If the chosen plan can't be added from here (no variant, no
     selling plan, unavailable) the button is disabled and the disclosure
     says so: a bare tracker is NEVER added.
   - The tracker is ALWAYS its live price (data-kb-price on its card) — the
     theme never subtracts from it. The offer (the first year of the chosen
     plan included) is delivered by automatic discounts in Shopify admin on
     the plan line at checkout, so "Today" is the tracker price and the
     plan's normal price is what renews after the included period: the
     plan line reads "First 12 months" (the included period in months)
     and, for its amount, the plan's own price for that period struck
     through (the term's price × the offer's cycle count × the quantity,
     from the plan's price, never typed) beside the "Included" pill. With
     the offer switched off (settings.ct_offer_enabled) the plan line is
     the first plan payment, charged today, with no struck figure and no
     "included" words.
   - The SIM line follows the chosen tracker: its card's
     data-kb-dual-network (snippets/ct-dual-network) shows the section's
     "Telstra + Optus dual-network SIM" line, or its "SIM fitted and
     activated" line, which names no carrier. Both are printed; nothing
     here writes their words.
   - There is no volume discount (Kris, D-152): the hardware line is the
     unit price times the quantity.
   - The starting plan is per tracker, then per set (defaultPlan()): a
     battery tracker (its card's data-kb-battery-line,
     snippets/ct-battery-plan) starts on Power Saver in every mode (Kris,
     2026-10-30) — on Real-Time where Power Saver can't be added at the
     term, as its own page falls back; any other on the set's plan
     (DEFAULT_PLAN: asset → Power Saver, vehicle and fleet → Real-Time),
     with no fallback, as before. Always at the Annual term.
     Until the buyer picks a plan in the dialog (state.planTouched) the
     plan follows the tracker they select; a plan they pick, and the term
     they swap to, hold when they change tracker. A pick is stored in
     sessionStorage, per page and builder for the visit (a fresh visit
     clears it), so a Back, Forward or reload
     that restores the controls knows the restored plan was theirs
     (restoredPick()); a plan the builder chose follows the restored
     tracker. Without storage the pick holds on the page it was made on,
     and a restore guesses (restoredPick() says how). The section renders
     the starting state of the tracker it opens on (its plan radio checked
     in the dialog, the receipt's lines at the annual term, in the strings
     update() writes), so the first render leaves them as painted: change
     the starting plan, its fallback or the term here and in the section
     together.
   - The Real-Time battery line (the card's data-kb-battery-line, the
     tracker page's words): under the dialog's Real-Time row while a
     battery tracker is selected, and under the receipt's plan line while
     it is paired with Real-Time, whoever chose the plan. The receipt's is
     a polite live region, so a card switch that brings it is announced.
   - Copy on the receipt: "included", never "free". The renewal is stated
     ONCE, in the sub-line under the plan line: annual "Then $100/yr
     ($8.33/mo). No minimum term." (an annual price always carries its
     monthly bracket), monthly "Then $10/mo. No minimum term — cancel any
     time." — only the monthly line says "cancel any time". The "Plan
     details" fold carries the longer disclosure ("… until you cancel") and
     the minimum total cost. The yearly swap link's "Save N%" is savings
     arithmetic, which the design brief's copy rules forbid — Kris ruled it
     in for this builder (2026-10-15); it is computed from the two plan
     prices, never typed.
   - The plan dialog: a pointer click on a plan row, Done, Escape and a
     click on the backdrop close it (arrow keys move the selection with it
     open), and focus returns to the "Change plan" link. With
     no showModal() it opens as a fixed panel (.is-fallback) and Escape is
     handled here.
   - A tracker the kit can't add (data-kb-available="false": its radio is
     disabled) can never be selected or added — out of stock, or
     outright-only (data-kb-outright), which no store plan covers. When no
     card at all can be added the summary says which of the two it is.
   - Quantity starts at 1 (fleet mode: the section's startQty) and stops at
     the chosen tracker's stock where stock is a ceiling (data-kb-stock on
     its card: inventory tracked and never oversold). There is no other
     limit. The vehicle and asset builders render no quantity control, so
     the stock message below is never written there. The ceiling is applied in
     one place, the top of update(), against the tracker selected at that
     moment: the opening quantity is held to the tracker the builder
     opens on (a ?tracker= landing included), and a change to a tracker
     with less stock than the quantity brings the quantity down. At the
     ceiling the stock line under the quantity control
     ([data-kb-out="stock"]) says more stock is on the way — the section's
     words (data.stockMax) — and a visually hidden role="status" node
     that is never hidden ([data-kb-out="stock-live"]) announces the same
     words. They carry no figure; the control itself stops at the stock.
     The fleet slider needs a top end above 1, so it shows only while the
     ceiling gives it one.
   - Selling past stock (the section's sell_past_stock, the fleet page
     only; Kris, 2026-10-30: anything listed there keeps selling when out
     of stock). Where a plug-in OBD lead's stock is the ceiling and its other
     twin can oversell at the same price, the card prints no data-kb-stock
     (no ceiling, no stock message) and carries that twin in data-kb-past-*
     (snippets/ct-kit-device-card). A kit goes in whole, as one tracker:
     as the lead while its quantity plus what the cart already holds of the
     lead is within the lead's stock, else as the twin (kitDevice()). That
     takes the cart, so it is settled in addKitToCart once the cart is
     read. The stock on the card is as of the page's render: a kit sent as
     the lead that the cart refuses, once undone, goes again as the twin,
     one time only, so a lead that sold out since can't strand the buyer. A
     second refusal is the twin's own. The request, the undo, the messages,
     the publishes and "ct:kit_add_to_cart" all name the tracker really
     sent. this.devices keeps the lead: the receipt and the card show it
     either way. The battery-mount twins are different products: their
     cards never carry data-kb-past-*, so `past` is null and neither
     kitDevice() nor the resend swaps them. The CT-V07 is set to "deny", so
     it stops at its stock unless it is set to keep selling.
   - The kit can't change under an add that is in flight: the tracker cards
     ignore clicks, and setState() / pick() (and so "ct:type-choose") are
     held and applied, in order, once the request has settled. The cart
     change that follows the add saves the fit answer against the tracker
     that was actually added (ct-fit-details.js).
   - Once /cart/add has answered without an error the add HAS happened:
     nothing after that point — a cart-update subscriber that throws, a
     notification that fails to render — may read as a failed add, or the
     buyer would be invited to add the kit a second time.
   - An add that fails leaves the cart as the buyer left it. Shopify answers
     an over-stock add with 422 AFTER adding what stock allows, and an add
     whose answer is lost may have gone in all the same. So the cart is read
     before the add (no add is sent without that read) and, on an error
     status or no usable answer, every cart line of the tracker and plan
     variants is put back to its quantity from that read — undoAdd(), the
     rule of the same name in assets/ct-product.js, matching lines by what
     they hold rather than by line key, one change at a time from the cart
     each change answers with. The message is then the builder's own:
     Shopify's "Only N items were added to your cart" is untrue once undone.
   - ?tracker=<device code or alias> on the page URL (e.g. ?tracker=ct-v09,
     set on the landing URLs of ads that price one tracker) opens the
     builder on that tracker when it is in the builder and in stock — on a
     fresh load only; a card the browser restored is the buyer's own choice
     and stands. An alias is read from the card of the twin that leads
     (data-kb-aliases, snippets/ct-kit-device-card and snippets/ct-lead):
     the group and both codes, so ?tracker=obd, ?tracker=ct-v08 and
     ?tracker=ct-v10 all open on whichever plug-in twin leads, and
     ?tracker=battery, ?tracker=ct-v07 and ?tracker=ct-v09 on whichever
     battery-mount twin does. A card's own key is matched before any alias
     (deviceByAlias()).
   - Controls the browser restores (Back, reload, tab restore) are the
     buyer's own earlier choices. Some browsers restore before this deferred
     script runs, which init sees as it reads the controls; Chromium
     restores AFTER it, silently, so resync() runs on pageshow and renders
     again when the controls no longer match what was last rendered.
   - Quantity N adds N trackers + N plans (every tracker needs its own SIM).
   - Cart pairing: both lines carry the kit line item properties from
     window.ctKitProperties (assets/ct-cart-lines.js) — `_ct_kit`,
     `_ct_kit_label`, and "For tracker" on the plan — so the cart page and
     the notification group the pair. The device code is ct-device-detect's
     (data-kb-pair-code, the same rule as the tracker page) and the plan
     key / title are the four the cart already knows: "power-saver",
     "power-saver-annual", "real-time", "real-time-annual". Do not rename
     them: sections/main-cart-items.liquid, cart-notification-product and
     ct-cart-offer-line read them. The plan line can also carry the visible
     "Billing" property, for the checkout's order summary ("First 12 months
     included, then $10/mo per tracker from month 13") — only while the
     theme setting ct_offer_billing_line is ticked (data.offer.billingLine)
     and the offer is on. Unticked, the plan line carries the three
     properties above and nothing else. The sentence, its price figure
     included, is window.ctKitBilling's and is never built here (it is
     handed the plan's price in cents): the tracker page must write the
     identical string for the same plan, or a repeat add of the kit would
     not merge into its plan line.
   - "What will it be fitted to?" (snippets/ct-fit-details.liquid, one
     shared picker in the summary — not rendered since the 2026-10-24 port,
     but driven here where a host renders one): shown only while the
     selected tracker is one we set up per vehicle (data-kb-fit) and can be
     added.
     updateFitDetails() writes the tracker's code to the select's
     data-ct-fit-device, offers only the options that tracker can be fitted
     to (data-ct-fit-only), and keeps each tracker's answer to itself.
     Saving is assets/ct-fit-details.js's job — a cart attribute, never a
     line item property.
   - Analytics: two Shopify customer events, published through
     window.ctTrack (assets/ct-base.js) — with no ctTrack on the page
     nothing is sent and nothing else changes.
     "ct:kit_builder_start": once per builder per page load, on the buyer's
     own first move in it — a trusted change or input event, or a trusted
     click on a quantity button. A selection the script made (setState(),
     "ct:type-choose", ?tracker=) or the browser restored is not a start,
     and neither is the add button: a kit added as it opened sends the add
     alone.
     "ct:kit_add_to_cart": once /cart/add has answered without an error —
     never on the refusal or lost-answer paths, which both undo.
     Both carry { set, device, plan, billing, qty }; the add's is the kit as
     it was sent. The theme sends nothing to GA4 itself: the custom pixel in
     ops/shopify-customer-events/ct-ga4-kit-events.js forwards them.

   window.CTKitBuilder
     init(scope)       mount any builders not yet mounted
     get(el)           the instance for a [data-kit-builder] element
     instances         all instances
   instance
     getState()        { set, device, plan, billing, qty, fitted }
     setState(partial) e.g. { device: 'ct-v06' } / { plan: 'power-saver', billing: 'annual' } / { qty: 10 }
                       (held until the request settles while an add is in flight)
     totals()          { todayCents, eachCents, laterCents, hardwareCents, … }
     refresh()         read the cards and controls again and re-render
   Events
     "ct:kit-change" (bubbles) from the root, detail = { state, totals }
     "ct:type-choose" on document (detail { type, device, set }): when a
     builder on the page holds that tracker (by code or alias) in stock it
     is selected and scrolled into view and the event is cancelled;
     otherwise the link that dispatched it navigates. */

(function () {
  'use strict';

  if (window.CTKitBuilder) return;

  var DESKTOP = 990;
  var instances = [];
  var forEach = Array.prototype.forEach;
  // The plan each set starts on (Kris, 2026-10-15); every other set starts
  // on Real-Time. A battery tracker starts on Power Saver whatever the set
  // (defaultPlan()). The section checks the same radio: change both together.
  var DEFAULT_PLAN = { asset: 'power-saver' };

  /* "$149", "$660.25"; with forceCents "$149.00" — the mockup's money(). */
  function money(cents, forceCents) {
    var value = Math.round(Number(cents) || 0);
    var whole = value % 100 === 0;
    var parts = (value / 100).toFixed(forceCents || !whole ? 2 : 0).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  /* The monthly equivalent of an annual price, in brackets and nothing
     more — "($8.33/mo)": the price over 12, to the cent, as
     snippets/ct-purchase-options works it out (Kris, 2026-10-11). */
  function perMonth(cents) {
    return '(' + money(Math.round((Number(cents) || 0) / 12), true) + '/mo)';
  }

  /* A plan price for a term: "$100/yr ($8.33/mo)" — an annual price always
     carries its monthly equivalent and nothing more — or "$10/mo". */
  function termPrice(cents, monthly) {
    return monthly ? money(cents) + '/mo' : money(cents) + '/yr ' + perMonth(cents);
  }

  /* The yearly saving against twelve monthly payments, as a whole
     percentage, from the two plans' own prices (never typed). NOTE: the
     design brief's copy rules forbid savings arithmetic; Kris ruled
     "Save 17%" in for this builder's swap link (2026-10-15). */
  function yearlySavingPct(monthlyCents, annualCents) {
    var twelve = (Number(monthlyCents) || 0) * 12;
    if (!twelve) return 0;
    return Math.round((1 - (Number(annualCents) || 0) / twelve) * 100);
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /* Whether a node already shows this HTML, compared as the browser writes
     it back: innerHTML does not escape quotes in text (esc()'s &#39; and
     &quot;) and does escape a no-break space, so the string as built never
     matches then — and a live region would be rewritten, and read out
     again, on every render. */
  var probe = null;
  function shows(node, html) {
    if (node.innerHTML === html) return true;
    probe = probe || document.createElement('div');
    probe.innerHTML = html;
    return node.innerHTML === probe.innerHTML;
  }

  function plural(count, one, many) {
    return count === 1 ? one : many;
  }

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* A plan the kit can add: linked here, its variant available and its
     selling plan resolved — without the selling plan it would go in as a
     one-time line: no subscription, no included year. */
  function planUsable(plan) {
    return Boolean(plan && plan.variantId && plan.sellingPlanId && plan.available !== false);
  }

  /* Whether the browser may have restored this page's controls: Back,
     Forward or a reload, not a fresh visit, which starts from the markup.
     Assumed so where Navigation Timing can't say. */
  function restoredLoad() {
    try {
      var entry = window.performance.getEntriesByType('navigation')[0];
      return !entry || entry.type === 'back_forward' || entry.type === 'reload';
    } catch (error) {
      return true;
    }
  }

  function urlTracker() {
    try {
      var value = new URLSearchParams(window.location.search).get('tracker');
      return value ? value.trim().toLowerCase() : '';
    } catch (error) {
      return '';
    }
  }

  function headerHeight() {
    var header =
      document.querySelector('.ct-header') ||
      document.querySelector('.shopify-section-header-sticky') ||
      document.querySelector('sticky-header');
    if (header && header.offsetHeight) return header.offsetHeight;
    var token = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--ct-header-h'), 10);
    return token || 72;
  }

  // Dawn's cart-notification renders only the line whose key it is handed
  // (the tracker's) and strips its .cart-item wrapper. The plan line
  // carries the offer, so after renderContents the host is rebuilt from
  // every added item's full line — by the ONE shared implementation in
  // assets/ct-cart-lines.js (window.ctRenderNotificationLines). This thin
  // wrapper has an identical twin in assets/ct-product.js; keep both
  // wrappers as is and change the helper, never the wrappers.
  function appendNotificationLines(cart, response) {
    if (typeof window.ctRenderNotificationLines !== 'function') return;
    try {
      window.ctRenderNotificationLines(cart, response);
    } catch (error) {
      // The add already succeeded; a line rebuild error must never read as a failed add.
      console.error(error);
    }
  }

  // Dawn's publish() runs its subscribers inline and hands back a promise
  // of their results. A subscriber that throws (now or later) is none of
  // the add's business: log it and carry on.
  function safePublish(eventName, data) {
    try {
      var result = publish(eventName, data);
      if (result && typeof result.catch === 'function') {
        result.catch(function (error) {
          console.error(error);
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  function KitBuilder(root) {
    this.root = root;
    this.ok = false;

    var dataEl = root.querySelector('[data-kb-json]');
    try {
      this.data = dataEl ? JSON.parse(dataEl.textContent) : null;
    } catch (error) {
      this.data = null;
    }
    if (!this.data || !this.data.plans) return;

    this.set = String(root.getAttribute('data-set') || 'vehicle').toLowerCase();
    this.fleet = this.set === 'fleet';
    this.offer = this.data.offer || {};
    this.devices = this.readDevices();
    if (!this.devices.length) return;

    // "What will it be fitted to?" — the shared ct-fit-details picker.
    // fitOptions is the full option list as rendered (updateFitDetails takes
    // the device-specific ones in and out); fitChoices holds each device
    // code's own answer, starting from the ones the cart already holds;
    // fitCode is the code the picker is showing now (null until the first
    // pass).
    this.fitSelect = root.querySelector('[data-ct-fit-details]');
    this.fitWrap = this.fitSelect ? this.fitSelect.closest('[data-ct-fit-wrap]') : null;
    this.fitOptions = this.fitSelect ? Array.prototype.slice.call(this.fitSelect.options) : [];
    this.fitChoices = this.savedFitChoices();
    this.fitCode = null;

    this.errorEl = root.querySelector('[data-kb-error]');
    // adding: an add is in flight. held: setState() / pick() calls made
    // meanwhile, applied in order when it settles.
    this.adding = false;
    this.held = [];
    // started: "ct:kit_builder_start" has been published for this builder.
    this.started = false;

    this.opener = root.querySelector('[data-kb-open-plan]');

    // planTouched: the plan was picked — in the dialog, through setState(),
    // or a pick the browser restored (restoredPick()) — so it no longer
    // follows the tracker (update()). planKey: where a pick is stored for
    // that restore, per page and builder.
    this.planKey = 'ct-kb-plan:' + window.location.pathname + ':' + (root.getAttribute('data-section-id') || '');
    // A fresh visit holds no pick: one stored on an earlier visit is
    // cleared, or a reload of this visit would bring it back as the buyer's.
    if (!restoredLoad()) {
      try {
        window.sessionStorage.removeItem(this.planKey);
      } catch (error) {
        // No storage: nothing was stored.
      }
    }
    this.state = {
      device: '',
      plan: DEFAULT_PLAN[this.set] || 'real-time' /* the set's plan, at the Annual term, until update() reads the tracker's */,
      billing: 'annual',
      qty: Math.max(1, parseInt(this.data.startQty, 10) || 1),
      deviceTouched: false,
      planTouched: false,
    };

    this.readControls();
    this.applyUrlTracker();
    this.bind();
    this.ok = true;
    // Set before the first render, so a "ct:kit-change" listener can already
    // reach the instance through CTKitBuilder.get(root).
    root.ctKitBuilder = this;
    this.update(true);
  }

  KitBuilder.prototype = {
    /* ------------------------------------------------------------- config */

    // The trackers, read from their cards (snippets/ct-kit-device-card).
    // `stock` is the quantity's ceiling for that tracker: data-kb-stock,
    // which the card prints only where stock is one; Infinity without it.
    // `past` is the twin a kit goes in as above the lead's stock and that
    // stock (data-kb-past-*, see the header), null on any other card.
    readDevices: function () {
      var list = [];
      forEach.call(this.root.querySelectorAll('[data-kb-device]'), function (card) {
        var variantId = parseInt(card.getAttribute('data-kb-variant'), 10) || null;
        var stock = parseInt(card.getAttribute('data-kb-stock'), 10);
        var pastStock = parseInt(card.getAttribute('data-kb-past-stock'), 10);
        var pastVariantId = parseInt(card.getAttribute('data-kb-past-variant'), 10) || null;
        list.push({
          key: String(card.getAttribute('data-kb-device') || '').toLowerCase(),
          code: card.getAttribute('data-kb-code') || '',
          title: card.getAttribute('data-kb-title') || card.getAttribute('data-kb-code') || '',
          deviceCode: card.getAttribute('data-kb-pair-code') || '',
          variantId: variantId,
          available: Boolean(variantId) && card.getAttribute('data-kb-available') === 'true',
          outright: card.getAttribute('data-kb-outright') === 'true',
          stock: stock > 0 ? stock : Infinity,
          past:
            pastStock > 0 && pastVariantId
              ? {
                  stock: pastStock,
                  variantId: pastVariantId,
                  code: card.getAttribute('data-kb-past-code') || '',
                  deviceCode: card.getAttribute('data-kb-past-pair-code') || '',
                }
              : null,
          price: parseInt(card.getAttribute('data-kb-price'), 10) || 0,
          url: card.getAttribute('data-kb-url') || '',
          fitDetails: card.getAttribute('data-kb-fit') === 'true',
          shipsNote: card.getAttribute('data-kb-ships-note') || '',
          dualNetwork: card.getAttribute('data-kb-dual-network') === 'true',
          batteryLine: card.getAttribute('data-kb-battery-line') || '',
          aliases: String(card.getAttribute('data-kb-aliases') || '')
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean),
          card: card,
          input: card.querySelector('input[type="radio"]'),
        });
      });
      return list;
    },

    deviceByKey: function (key) {
      for (var i = 0; i < this.devices.length; i += 1) {
        if (this.devices[i].key === key) return this.devices[i];
      }
      return null;
    },

    // A tracker named from outside the builder (?tracker=, "ct:type-choose"):
    // by its key first, else by an alias on its card (data-kb-aliases, the
    // lead twin's group and codes), so either twin's code, or the group's
    // name, finds the twin that leads.
    deviceByAlias: function (key) {
      key = String(key || '').toLowerCase();
      if (!key) return null;
      var device = this.deviceByKey(key);
      if (device) return device;
      for (var i = 0; i < this.devices.length; i += 1) {
        if (this.devices[i].aliases.indexOf(key) !== -1) return this.devices[i];
      }
      return null;
    },

    // The most the quantity can be: the selected tracker's stock where stock
    // is a ceiling, Infinity where it is not (and while no tracker is
    // selected). Read from the device each time, never kept, so it follows
    // the selection and a refresh(). update() is the one place that holds
    // the quantity to it.
    maxQty: function () {
      var device = this.deviceByKey(this.state.device);
      return device ? device.stock : Infinity;
    },

    // The plan for a tier ("power-saver" | "real-time") and a cadence. The
    // blob keys the four plans as the cart knows them: the tier alone is the
    // monthly plan, "<tier>-annual" the yearly one.
    planOf: function (tier, billing) {
      return this.data.plans[billing === 'annual' ? tier + '-annual' : tier] || null;
    },

    // The plan tier a tracker starts on at a term (see the header): Power
    // Saver for a battery tracker — its card carries the Real-Time battery
    // line — on any page (Kris, 2026-10-30), or Real-Time where Power Saver
    // can't be added at that term, as the tracker's own page falls back
    // (ct-purchase-options); else the set's. The section starts the
    // tracker it opens on by the same rule.
    defaultPlan: function (device, billing) {
      if (device && device.batteryLine) {
        return planUsable(this.planOf('power-saver', billing)) ? 'power-saver' : 'real-time';
      }
      return DEFAULT_PLAN[this.set] || 'real-time';
    },

    // The buyer picked the plan now in the state: it holds from here on,
    // and is stored (sessionStorage) so a restore of this page knows it was
    // theirs. Without storage it holds on this page only.
    planPicked: function () {
      this.state.planTouched = true;
      try {
        window.sessionStorage.setItem(this.planKey, this.state.plan);
      } catch (error) {
        // No storage: restoredPick() falls back to its guess.
      }
    },

    // Whether a plan read from the controls is the buyer's own pick. Only
    // on a load the browser may have restored (restoredLoad()): a fresh
    // visit starts from the markup, which holds no pick. Then it is the
    // buyer's when it is the plan they last picked in this builder on this
    // visit (planPicked(); a fresh visit clears it). Where storage can't be read, the guess the builder
    // made before it had storage: any plan but the one the tracker starts
    // on — which misses a pick that is that plan, and counts the plan of a
    // restored tracker that is no longer in stock as a pick.
    restoredPick: function (value) {
      if (!restoredLoad()) return false;
      var stored;
      try {
        stored = window.sessionStorage.getItem(this.planKey) || '';
      } catch (error) {
        return value !== this.defaultPlan(this.deviceByKey(this.state.device) || this.devices[0], this.state.billing);
      }
      return stored === value;
    },

    selectable: function (key) {
      var device = this.deviceByKey(String(key || '').toLowerCase());
      return Boolean(device && device.available);
    },

    // The card the markup starts on (Liquid's "Start selected" pick), else
    // the first tracker in stock, else the first card.
    defaultDevice: function () {
      var available = this.devices.filter(function (device) {
        return device.available;
      });
      var marked = available.filter(function (device) {
        return device.input && device.input.defaultChecked;
      })[0];
      return (marked || available[0] || this.devices[0]).key;
    },

    // The controls as they stand: the markup's defaults on a fresh load,
    // the buyer's own earlier choices when the browser restored them. The
    // quantity is read as it stands, not held to stock: the tracker can
    // still change before the render (?tracker=), and update() holds it to
    // the one the builder opens on. The term has no control the browser
    // could restore (it is swapped by a button), so it stays as it is.
    readControls: function () {
      var s = this.state;
      var root = this.root;
      var checked = this.devices.filter(function (device) {
        return device.available && device.input && device.input.checked;
      })[0];
      if (checked) s.device = checked.key;
      else if (!this.selectable(s.device)) s.device = this.defaultDevice();

      var plan = root.querySelector('[data-kb-plan] input:checked');
      if (plan && this.data.plans[plan.value]) {
        s.plan = plan.value;
        // The buyer's own pick, restored by the browser: it holds again.
        // Any other plan follows the tracker (update()).
        if (this.restoredPick(plan.value)) s.planTouched = true;
      }
      var qty = root.querySelector('[data-kb-qty-input]');
      if (qty) this.setQty(qty.value);
    },

    // ?tracker=ct-v09 (or an alias: ?tracker=obd) — only while the checked
    // card is still the markup's default, and only for a tracker the kit
    // can add.
    applyUrlTracker: function () {
      var wanted = this.deviceByAlias(urlTracker());
      if (!wanted || !this.selectable(wanted.key)) return;
      var restored = this.devices.some(function (device) {
        return device.input && device.input.checked && !device.input.defaultChecked;
      });
      if (restored) return;
      this.state.device = wanted.key;
      this.state.deviceTouched = true;
    },

    /* ------------------------------------------------------------- totals */

    totals: function () {
      var s = this.state;
      var device = this.deviceByKey(s.device) || this.devices[0];
      var plan = this.planOf(s.plan, s.billing) || this.planOf('real-time', s.billing) || {};
      var qty = s.qty;

      // Whether the plan can be added at all (planUsable(), above).
      var usable = planUsable(plan);
      var offerOn = Boolean(this.offer.enabled);

      // No volume discount (D-152): the hardware line is unit × quantity.
      var unitCents = device.price;
      var hardwareCents = unitCents * qty;
      var eachCents = unitCents;
      var planUnitCents = Number(plan.price) || 0;
      var laterCents = planUnitCents * qty;
      // Offer on: year one is the hardware line. Offer off: the plan's first
      // payment is charged today as well.
      var todayCents = hardwareCents + (usable && !offerOn ? laterCents : 0);

      return {
        device: device,
        plan: plan,
        planName: plan.name || '',
        planRate: plan.rate || '',
        planLabel: plan.title || '',
        billing: s.billing,
        per: plan.per || (s.billing === 'annual' ? 'yr' : 'mo'),
        qty: qty,
        unitCents: unitCents,
        eachCents: eachCents,
        hardwareCents: hardwareCents,
        todayCents: todayCents,
        planUnitCents: planUnitCents,
        laterCents: laterCents,
        planUsable: usable,
        offerOn: offerOn,
        addable: Boolean(device.available && usable),
      };
    },

    /* ------------------------------------------------------------- update */

    out: function (key, html) {
      var nodes = this.root.querySelectorAll('[data-kb-out="' + key + '"]');
      forEach.call(nodes, function (node) {
        if (!shows(node, html)) node.innerHTML = html;
      });
      return nodes;
    },

    outValue: function (key, html, initial) {
      var nodes = this.root.querySelectorAll('[data-kb-out="' + key + '"]');
      forEach.call(nodes, function (node) {
        if (shows(node, html)) return;
        node.innerHTML = html;
        if (!initial) {
          node.classList.remove('is-changed');
          void node.offsetWidth;
          node.classList.add('is-changed');
        }
      });
    },

    update: function (initial) {
      var self = this;
      var root = this.root;
      var s = this.state;
      // The ceiling follows the chosen tracker, so the quantity is brought
      // within it here, before anything is priced — and only here (setQty
      // does not): the opening quantity is held to the tracker the builder
      // opens on, and a change to a tracker with less stock than the
      // quantity brings the quantity down. `capped` is false for a tracker
      // with no stock ceiling (max is then Infinity).
      var max = this.maxQty();
      var capped = max !== Infinity;
      if (s.qty > max) s.qty = max;
      // The stock message belongs to a quantity control: a builder without
      // one (every mode but fleet) never says it.
      var hasQty = Boolean(root.querySelector('[data-kb-qty-input], [data-kb-slider]'));
      var atStock = hasQty && capped && s.qty >= max;
      // Until the buyer picks a plan, it follows the tracker selected at
      // this moment (defaultPlan(), Kris 2026-10-30) — here, so every way
      // the tracker changes (a card, setState(), pick(), ?tracker=, a
      // refresh) takes it, and so does a term swap, for defaultPlan()'s
      // fallback. The tier only: the term stays as it is.
      if (!s.planTouched) s.plan = this.defaultPlan(this.deviceByKey(s.device) || this.devices[0], s.billing);
      var t = this.totals();
      var monthly = s.billing !== 'annual';
      var per = t.per;
      var each = t.qty > 1 ? ' each' : '';
      var planPrice = money(t.planUnitCents) + '/' + per;
      var code = t.device.code;

      var cyclesMonthly = Number(this.offer.cyclesMonthly) || 12;
      var cyclesAnnual = Number(this.offer.cyclesAnnual) || 1;
      var includedMonths = monthly ? cyclesMonthly : cyclesAnnual * 12;
      var afterShort = monthly ? 'from month ' + (cyclesMonthly + 1) : 'from year ' + (cyclesAnnual + 1);
      // The included period in the term's own count: "first year" while
      // it is one year, else "first 24 months" / "first 2 years". The
      // dialog's lede says it; the plan line says it in months
      // ("First 12 months", includedMonths), as the section prints it for
      // the annual term it opens on.
      var includedPeriod = monthly
        ? cyclesMonthly === 12 ? 'first year' : 'first ' + cyclesMonthly + ' months'
        : cyclesAnnual === 1 ? 'first year' : 'first ' + cyclesAnnual + ' years';
      var includedCycles = monthly ? cyclesMonthly : cyclesAnnual;
      var includedClause =
        'Your ' + includedPeriod + (includedPeriod === 'first year' ? ' is' : ' are') + ' included either way.';

      /* tracker cards */
      this.devices.forEach(function (device) {
        var selected = device.available && device.key === s.device;
        device.card.classList.toggle('is-selected', selected);
        if (device.input && device.input.checked !== selected) device.input.checked = selected;
      });

      /* the plan dialog: two equal rows, each priced for the current term.
         Offer on, the price is what follows the included period ("Then
         …"); offer off, just the price. A plan with no variant has no
         price: a dash, never money() of nothing. */
      forEach.call(root.querySelectorAll('[data-kb-plan]'), function (row) {
        var key = row.getAttribute('data-kb-plan');
        var selected = key === s.plan;
        row.classList.toggle('is-selected', selected);
        var input = row.querySelector('input');
        if (input && input.checked !== selected) input.checked = selected;

        var priceEl = row.querySelector('[data-kb-plan-price]');
        if (!priceEl) return;
        var rowPlan = self.planOf(key, s.billing);
        var rowPrice = rowPlan && rowPlan.variantId ? termPrice(rowPlan.price, monthly) : '—';
        var rowHtml = (t.offerOn ? 'Then ' : '') + '<span class="ct-mono">' + esc(rowPrice) + '</span>';
        if (priceEl.innerHTML !== rowHtml) priceEl.innerHTML = rowHtml;
      });
      this.out(
        'dialog-lede',
        esc(
          (t.offerOn
            ? includedClause + ' This is the plan that carries on after it, '
            : 'This is the plan that goes with your tracker, ') + (monthly ? 'billed monthly.' : 'billed yearly.')
        )
      );

      /* quantity controls — their max is the ceiling, or none at all */
      forEach.call(root.querySelectorAll('[data-kb-qty-input]'), function (input) {
        if (capped) input.setAttribute('max', String(max));
        else input.removeAttribute('max');
        if (document.activeElement !== input || (input.value !== '' && parseInt(input.value, 10) !== s.qty)) {
          input.value = String(s.qty);
        }
      });
      forEach.call(root.querySelectorAll('[data-kb-qty="-1"]'), function (button) {
        button.disabled = s.qty <= 1;
      });
      forEach.call(root.querySelectorAll('[data-kb-qty="1"]'), function (button) {
        button.disabled = s.qty >= max;
      });
      // The slider needs a top end above 1: shown with the ceiling as its
      // max, hidden (as the section renders it) for a tracker with no
      // ceiling or a ceiling of 1, where there is nothing to slide. The max
      // goes on before the value, which the browser holds within it.
      var slides = capped && max > 1;
      forEach.call(root.querySelectorAll('[data-kb-slider]'), function (slider) {
        var wrap = slider.closest('.ct-kb__slider-wrap');
        if (wrap) wrap.hidden = !slides;
        if (!slides) return;
        slider.setAttribute('max', String(max));
        slider.value = String(s.qty);
        slider.style.setProperty('--kb-fill', (((s.qty - 1) / (max - 1)) * 100).toFixed(2) + '%');
        slider.setAttribute('aria-valuetext', s.qty + plural(s.qty, ' vehicle', ' vehicles'));
      });
      // The stock message — the section's own words (data.stockMax, no
      // figure in them) while the quantity is at the chosen tracker's
      // ceiling, nothing otherwise — goes to two nodes. SEEN: the stock
      // line under each quantity control ([data-kb-out="stock"], no role),
      // hidden while it is empty. ANNOUNCED: the one visually hidden
      // role="status" node ([data-kb-out="stock-live"]), which is never
      // hidden — a status node that is display:none until the moment it is
      // filled is not reliably read out. Each is written only when its text
      // changes, as out() does, so the message is announced once — compared
      // as text, which reads back as written whatever characters the copy
      // holds.
      var stockText = atStock ? String(this.data.stockMax || '') : '';
      forEach.call(root.querySelectorAll('[data-kb-out="stock"]'), function (node) {
        if (node.textContent !== stockText) node.textContent = stockText;
        node.hidden = !stockText;
      });
      forEach.call(root.querySelectorAll('[data-kb-out="stock-live"]'), function (node) {
        if (node.textContent !== stockText) node.textContent = stockText;
      });
      /* fitted-to picker: only the trackers we set up per vehicle */
      this.updateFitDetails();

      /* the receipt */
      var today = money(t.todayCents, true);
      var included = t.offerOn && t.planUsable;
      var many = t.qty > 1;

      // The tracker line: "3 × Slim Hardwired Vehicle Tracker", at the
      // live price times the quantity (no volume discount, D-152).
      this.out('tracker-name', (many ? '<span class="ct-mono">' + t.qty + ' ×</span> ' : '') + esc(t.device.title));
      this.outValue('tracker-amt', money(t.hardwareCents, true), initial);

      // The SIM line: the chosen tracker's (see the header).
      forEach.call(root.querySelectorAll('[data-kb-sim]'), function (node) {
        node.hidden = (node.getAttribute('data-kb-sim') === 'dual') !== t.device.dualNetwork;
      });

      // The plan line: "Tracking plan · Real-Time (10s updates)". Offer
      // on, "First 12 months" under the name and, for its amount, the
      // plan's own price for those months struck through and the
      // "Included" pill (the section prints the same for the annual term
      // it opens on); offer off, the plan's first payment, charged today,
      // and nothing else. A plan the kit can't add: a dash (the button is
      // disabled and the disclosure says why).
      this.out('plan-name', esc(t.planName || t.planLabel) + (t.planRate ? ' (' + esc(t.planRate) + ')' : ''));
      var periodNodes = this.out('plan-period', esc('First ' + includedMonths + ' months'));
      forEach.call(periodNodes, function (node) {
        node.hidden = !included;
      });
      var planAmt = !t.planUsable
        ? '—'
        : included
          ? '<s class="ct-kb__was"><span class="ct-visually-hidden">Plan price </span>' +
            money(t.laterCents * includedCycles, true) +
            '</s> <span class="ct-badge ct-badge--offer">Included</span>'
          : money(t.laterCents, true);
      this.outValue('plan-amt', planAmt, initial);
      forEach.call(root.querySelectorAll('[data-kb-plan-line]'), function (node) {
        node.classList.toggle('ct-kb__line--incl', included);
      });

      // The ONE renewal sentence, under the plan line: the plan's price for
      // the term times the quantity. Only the monthly line says "cancel any
      // time"; the fold's disclosure carries "until you cancel".
      var renewal = '';
      if (t.planUsable) {
        renewal =
          'Then ' + termPrice(t.laterCents, monthly) + (many ? ' for ' + t.qty + ' trackers' : '') +
          (monthly ? '. No minimum term — cancel any time.' : '. No minimum term.');
      }
      this.out('renewal', esc(renewal));

      // The term swap link: the other term's price for one tracker, and
      // on the yearly side the saving against twelve monthly payments
      // (yearlySavingPct: Kris ruled it in; see the header). Priced for the
      // plan the swap lands on: a plan the buyer has not picked follows the
      // tracker at the other term too (defaultPlan()'s fallback), and may
      // change tier — then no saving is worked out, as two tiers' prices
      // are no saving.
      var otherTerm = monthly ? 'annual' : 'monthly';
      var otherTier = s.planTouched ? s.plan : this.defaultPlan(t.device, otherTerm);
      var other = this.planOf(otherTier, otherTerm);
      var otherPriced = Boolean(other && other.variantId);
      var swap;
      if (monthly) {
        var pct = otherPriced && otherTier === s.plan ? yearlySavingPct(t.plan.price, other.price) : 0;
        swap =
          'Prefer yearly?' +
          (otherPriced ? ' (' + money(other.price) + '/yr' + each + (pct > 0 ? ' · Save ' + pct + '%' : '') + ')' : '');
      } else {
        swap = 'Prefer monthly?' + (otherPriced ? ' (' + money(other.price) + '/mo' + each + ')' : '');
      }
      forEach.call(root.querySelectorAll('[data-kb-swap-term]'), function (button) {
        if (button.textContent !== swap) button.textContent = swap;
        button.setAttribute('aria-label', (monthly ? 'Switch to yearly billing, ' : 'Switch to monthly billing, ') + swap);
      });

      this.outValue('total', today, initial);

      /* the "Plan details" fold: the disclosure sentence (the live site's
         pattern, from real prices), then the minimum total cost on its own
         line — blank, and hidden, while nothing can be added */
      var minimum = t.device.available && t.planUsable ? 'Minimum total cost ' + money(t.todayCents) + '.' : '';
      var disclosure;
      if (!t.device.available) {
        // No card can be added, so the first one stands in: say why IT can't.
        disclosure = t.device.outright
          ? code + ' is sold without a plan, so it can’t be added as a kit — buy it on the tracker’s page.'
          : code + ' is out of stock right now, so it can’t be added to the cart yet.';
      } else if (!t.planUsable) {
        disclosure = 'The ' + t.planLabel + ' plan isn’t available to add here — choose it on the tracker’s page instead.';
      } else if (!t.offerOn) {
        disclosure =
          t.planLabel + ': ' + planPrice + (monthly ? '' : ' ' + perMonth(t.planUnitCents)) + each +
          ', renews until you cancel — no minimum term.';
      } else {
        // The included period, in months as the offer counts them, then
        // what follows it. An annual renewal price carries its monthly
        // bracket, as every annual price does.
        disclosure =
          'Your first ' + includedMonths + ' months of the ' + t.planLabel + ' plan are included with each tracker. ' +
          afterShort.charAt(0).toUpperCase() + afterShort.slice(1) + ' it renews at ' + planPrice +
          (monthly ? '' : ' ' + perMonth(t.planUnitCents)) + each +
          ' until you cancel — no minimum term.';
      }
      this.out('disclosure', esc(disclosure));
      var minimumNodes = this.out('minimum', esc(minimum));
      forEach.call(minimumNodes, function (node) {
        node.hidden = !minimum;
      });

      /* the tracker's own note (a kit that ships without batteries), in its
         own node so a quantity change doesn't re-announce it */
      var noteNodes = this.out('device-note', esc(t.device.shipsNote || ''));
      forEach.call(noteNodes, function (node) {
        node.hidden = !t.device.shipsNote;
      });

      /* the Real-Time battery line (see the header): under the dialog's
         Real-Time row while a battery tracker is selected, and under the
         receipt's plan line while it is paired with Real-Time. The
         receipt's is a live region, so it is emptied, never hidden: a
         region hidden until it is filled is not reliably read out. */
      var batteryLine = t.device.batteryLine || '';
      forEach.call(this.out('battery-rec', esc(batteryLine)), function (node) {
        node.hidden = !batteryLine;
      });
      var batteryWarn = batteryLine && String(t.plan.key || '').indexOf('real-time') === 0 ? batteryLine : '';
      this.out('battery-note', esc(batteryWarn));

      /* the quiet lines (the fine print under the summary is static: the
         section renders it and nothing here rewrites it) */
      this.out('per-today', money(t.eachCents, true));
      this.out('per-later-label', 'Per vehicle ' + esc(t.offerOn ? afterShort : monthly ? 'every month' : 'every year'));
      this.out('per-later', planPrice + (monthly ? '' : ' ' + perMonth(t.planUnitCents)));

      /* mobile bar: "Total today $139.00 · Add to cart" */
      this.out('bar-total', today);

      /* signpost: the selected tracker's own page */
      var signpost = root.querySelector('[data-kb-signpost]');
      if (signpost && t.device.url) {
        signpost.setAttribute('href', t.device.url);
        signpost.setAttribute('aria-label', 'Choose that on the tracker’s page: ' + code);
      }

      if (this.errorEl) this.errorEl.hidden = true;
      this.updateCta(t);

      // What this render was based on — resync() compares against it.
      this.rendered = this.snapshot();

      root.dispatchEvent(new CustomEvent('ct:kit-change', { bubbles: true, detail: { state: this.getState(), totals: t } }));
      scheduleBars();
    },

    updateCta: function (totals) {
      var t = totals || this.totals();
      var label = 'Add to cart';
      var disabled = this.adding;
      if (!t.device.available) {
        // Nothing in the builder can be added. An outright-only tracker is
        // not out of stock: it is bought, without a plan, on its own page.
        label = t.device.outright ? 'Buy from the tracker’s page' : 'Out of stock';
        disabled = true;
      } else if (!t.planUsable) {
        // The kit can't deliver its included year without the plan in the
        // cart, so a bare tracker is never added from here.
        label = 'Plan not available here';
        disabled = true;
      }
      forEach.call(this.root.querySelectorAll('[data-kb-add]'), function (button) {
        button.disabled = disabled;
        if (button.textContent !== label) button.textContent = label;
      });
    },

    // The answers the cart already holds, as { CODE: answer } — the select's
    // data-ct-fit-saved (snippets/ct-fit-details.liquid): "CODE=answer"
    // entries joined by ";;", each split on its first "=".
    savedFitChoices: function () {
      var choices = {};
      var raw = this.fitSelect ? this.fitSelect.getAttribute('data-ct-fit-saved') || '' : '';
      raw.split(';;').forEach(function (entry) {
        var at = entry.indexOf('=');
        if (at > 0) choices[entry.slice(0, at)] = entry.slice(at + 1);
      });
      return choices;
    },

    // "What will it be fitted to?" follows the selected tracker: shown for a
    // tracker we set up per vehicle that the kit can add, hidden (and so
    // never saved — ct-fit-details.js skips a hidden picker) for the rest.
    // The answer is saved against the code written here, so each tracker
    // keeps its own: on a switch the outgoing tracker's answer is remembered
    // and the incoming tracker's brought back — the one chosen here, else
    // the one the cart holds for it — or blank. Setting the value here fires
    // no change event, so nothing is saved by the switch itself.
    updateFitDetails: function () {
      if (!this.fitSelect || !this.fitWrap) return;
      var device = this.deviceByKey(this.state.device) || this.devices[0];
      var code = device.fitDetails && device.deviceCode && device.available ? device.deviceCode : '';
      var select = this.fitSelect;

      if (code !== this.fitCode) {
        // First pass (init, and again after resync): an answer the browser
        // restored (Back, reload) wins, the one the cart holds for this
        // tracker is the fallback — either only if this tracker can take it
        // (the selectedIndex guard below).
        var answer = this.fitCode === null ? select.value || this.fitChoices[code] || '' : this.fitChoices[code] || '';
        if (this.fitCode) this.fitChoices[this.fitCode] = select.value;
        this.fitCode = code;
        select.setAttribute('data-ct-fit-device', code);

        // Options limited to other trackers leave the list (removed, not
        // hidden — Safari still lists a hidden option). Appending in the
        // rendered order keeps that order.
        this.fitOptions.forEach(function (option) {
          var only = option.getAttribute('data-ct-fit-only');
          if (!only || only.split(' ').indexOf(code) !== -1) select.appendChild(option);
          else if (option.parentNode) option.parentNode.removeChild(option);
        });

        select.value = answer;
        if (select.selectedIndex < 0) select.value = '';
      }

      this.fitWrap.hidden = !code;
    },

    /* ------------------------------------------------------------- events */

    // Takes a whole number the script can count with (not a number, or one
    // too large to be exact, is refused and the quantity stays as it was),
    // 1 at the least. Stock is not applied here: every caller renders
    // next, and update() holds the quantity to the chosen tracker's
    // ceiling — once the tracker is settled, which it is not yet when the
    // controls are first read.
    setQty: function (value) {
      var qty = parseInt(value, 10);
      if (!Number.isSafeInteger(qty)) return false;
      this.state.qty = Math.max(1, qty);
      return true;
    },

    // What a render is based on: the checked card and plan radio and the
    // quantity as typed (the term is a button, not a control the browser
    // restores). update() stores it; resync() compares against it.
    snapshot: function () {
      var root = this.root;
      var device = root.querySelector('[data-kb-device] input:checked');
      var plan = root.querySelector('[data-kb-plan] input:checked');
      var qty = root.querySelector('[data-kb-qty-input]');
      return [device ? device.value : '', plan ? plan.value : '', qty ? qty.value : ''].join('|');
    },

    // pageshow: the controls no longer match the last render when the
    // browser restored them after this script ran (Chromium — Back without
    // the back/forward cache, tab restore, duplicate tab), with no change
    // event. They are the buyer's own earlier choices, so the builder
    // renders from them. On a normal first load nothing differs and nothing
    // is rendered or announced again.
    resync: function () {
      if (!this.ok || typeof this.rendered !== 'string') return;
      if (this.snapshot() === this.rendered) return;
      // The fit picker starts over, so updateFitDetails() takes its first
      // pass again: it keeps the answer the browser restored when the
      // restored tracker can take it, else the one the cart holds.
      this.fitCode = null;
      this.fitChoices = this.savedFitChoices();
      // The plan too: whether it is the buyer's pick is read again from
      // the restored controls (readControls(), restoredPick()), not kept
      // from the defaults init read.
      this.state.planTouched = false;
      this.readControls();
      this.update(true);
    },

    bind: function () {
      var self = this;
      var root = this.root;

      root.addEventListener('submit', function (event) {
        event.preventDefault();
      });

      // The Vehicles / Assets switch above the builder: its current face is
      // a link to this band (to this builder where the band has no id), so
      // that it still works as a link with the script off. With the script
      // on it does nothing, as a checked radio does — the page must not move
      // under the pointer. (Cancelled here, on the switch, so ct-base.js's
      // same-page anchor scroll stands aside.) The two tracker collections
      // are the exception: assets/ct-trackers.js takes the click first, in
      // the capture phase, and lands on the top of the band (Kris, 2026-10-30).
      var band = root.closest('.ct-section');
      var faces = band ? band.querySelector('[data-trk-switch]') : null;
      if (faces) {
        faces.addEventListener('click', function (event) {
          if (event.target.closest && event.target.closest('a[aria-current]')) event.preventDefault();
        });
      }

      root.addEventListener('change', function (event) {
        var input = event.target;
        var s = self.state;
        if (!input || !input.matches) return;
        if (input.matches('[data-kb-device] input[type="radio"]')) {
          // Belt and braces for the click guard below: a card change that
          // reaches here while an add is in flight is put back, not taken.
          if (self.adding) {
            self.update();
            return;
          }
          if (!self.selectable(input.value)) return;
          s.device = String(input.value).toLowerCase();
          s.deviceTouched = true;
        } else if (input.matches('[data-kb-plan] input[type="radio"]')) {
          if (!self.data.plans[input.value]) return;
          s.plan = input.value;
          self.planPicked();
          // Not closed here: arrow keys on the radios fire `change` too, and
          // a keyboard user must be able to look at the other plan without
          // committing. A pointer click on a row closes it (bind, below).
        } else if (input.matches('[data-kb-qty-input]')) {
          self.setQty(input.value);
          input.value = String(s.qty);
        } else if (!input.matches('[data-ct-fit-details]')) {
          return;
        }
        self.update();
      });

      root.addEventListener('input', function (event) {
        var input = event.target;
        if (!input || !input.matches) return;
        if (input.matches('[data-kb-slider]')) {
          self.setQty(input.value);
          self.update();
        } else if (input.matches('[data-kb-qty-input]')) {
          if (self.setQty(input.value)) self.update();
        }
      });

      root.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;

        // The card can't change while an add is in flight: the cart change
        // that follows the add saves the fit answer shown at that moment
        // against the tracker shown at that moment (ct-fit-details.js). A
        // cancelled click leaves the checked card checked and fires no
        // change event. The script API is held the same way (setState(),
        // pick() and so the "ct:type-choose" listener).
        if (self.adding && target.matches('[data-kb-device] input')) {
          event.preventDefault();
          return;
        }

        var el = target.closest('[data-kb-qty]');
        if (el) {
          self.setQty(self.state.qty + parseInt(el.getAttribute('data-kb-qty'), 10));
          self.update();
          return;
        }

        el = target.closest('[data-kb-swap-term]');
        if (el) {
          self.state.billing = self.state.billing === 'annual' ? 'monthly' : 'annual';
          self.update();
          return;
        }

        el = target.closest('[data-kb-open-plan]');
        if (el) {
          self.openDialog();
          return;
        }

        el = target.closest('[data-kb-done]');
        if (el) {
          self.closeDialog();
          return;
        }

        // A pointer click on a plan row chooses and closes (detail is 0 for
        // a keyboard-synthesised click: Space on a radio keeps the dialog
        // open). Deferred so the radio's own `change` has rendered first;
        // closing returns focus to "Change plan". A click on the row
        // already checked fires no `change`, yet it is the buyer's pick
        // all the same: the plan holds from now on (a click on another
        // row is taken by its `change`), and the receipt renders again,
        // as the term swap link prices a picked plan's own other term.
        el = target.closest('[data-kb-plan]');
        if (el) {
          if (el.getAttribute('data-kb-plan') === self.state.plan && !self.state.planTouched) {
            self.planPicked();
            self.update();
          }
          if (event.detail > 0) {
            window.setTimeout(function () {
              self.closeDialog();
            }, 0);
          }
          return;
        }

        el = target.closest('[data-kb-add]');
        if (el) {
          self.addKitToCart(el);
          return;
        }

        el = target.closest('[data-kb-review]');
        if (el) {
          self.showSummary();
          return;
        }

        // A click on the dialog's backdrop (the dialog element itself,
        // outside its inner panel) closes it.
        var dialog = target.closest('[data-kb-dialog]');
        if (dialog && target === dialog) self.closeDialog();
      });

      // Escape in the fallback dialog (no showModal); a real modal dialog
      // closes itself on Escape and fires `close`, taken below.
      root.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        var dialog = root.querySelector('[data-kb-dialog]');
        if (dialog && dialog.hasAttribute('open') && dialog.classList.contains('is-fallback')) {
          event.preventDefault();
          self.closeDialog();
        }
      });

      // However the dialog closed (Done, a choice, Escape, the backdrop),
      // focus goes back to the "Change plan" link that opened it.
      var planDialog = root.querySelector('[data-kb-dialog]');
      if (planDialog) {
        planDialog.addEventListener('close', function () {
          self.focusOpener();
        });
      }

      // "ct:kit_builder_start" — the buyer's own first move in this builder.
      // Bound after the listeners above, so the state it reports already
      // holds that move. isTrusted keeps out an event a script dispatched;
      // what the script sets itself (setState(), pick(), ?tracker=) and
      // what the browser restores fire no event here at all.
      // A radio or a select fires `input` first and `change` straight
      // after, and the listeners above take those on `change`: their
      // `input` is passed over, so the start is published on the `change`
      // with the new choice in it. Only the two controls taken on `input`
      // (the slider, the quantity field) start on it.
      var start = function (event) {
        if (self.started || event.isTrusted !== true) return;
        var target = event.target;
        if (!target || !target.matches) return;
        if (event.type === 'input' && !target.matches('[data-kb-slider], [data-kb-qty-input]')) return;
        // Of the buttons, only the ones that change the kit: a quantity
        // step or the term swap. Opening the dialog changes nothing.
        if (event.type === 'click' && !target.closest('[data-kb-qty], [data-kb-swap-term]')) return;
        self.started = true;
        self.track('kit_builder_start', self.trackData());
      };
      root.addEventListener('change', start);
      root.addEventListener('input', start);
      root.addEventListener('click', start);
    },

    showSummary: function () {
      var summary = this.root.querySelector('[data-kb-summary]');
      if (summary) summary.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    },

    /* ------------------------------------------------------- plan dialog */

    // Opens the plan chooser as a modal; without showModal() (an old
    // browser), as a fixed panel over the page (.is-fallback, styled in
    // ct-kit-builder.css). Focus lands on the checked plan.
    openDialog: function () {
      var dialog = this.root.querySelector('[data-kb-dialog]');
      if (!dialog) return;
      if (typeof dialog.showModal === 'function') {
        if (!dialog.hasAttribute('open')) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.classList.add('is-fallback');
      }
      var checked = dialog.querySelector('input:checked') || dialog.querySelector('input');
      if (checked) checked.focus({ preventScroll: true });
    },

    closeDialog: function () {
      var dialog = this.root.querySelector('[data-kb-dialog]');
      if (!dialog || !dialog.hasAttribute('open')) return;
      if (typeof dialog.close === 'function' && !dialog.classList.contains('is-fallback')) {
        dialog.close(); // fires `close`, which returns focus to the opener
      } else {
        dialog.removeAttribute('open');
        dialog.classList.remove('is-fallback');
        this.focusOpener();
      }
    },

    focusOpener: function () {
      if (this.opener) this.opener.focus({ preventScroll: true });
    },

    /* --------------------------------------------------------------- cart */

    // The tracker a kit of `qty` goes in as, given `cart` as it stands. A
    // card with no `past` is its own tracker. One with it (see the header)
    // stays the lead while the quantity plus what the cart already holds of
    // the lead's variant is within the lead's stock; above that it is the
    // twin (twinOf()).
    kitDevice: function (device, qty, cart) {
      var past = device.past;
      if (!past) return device;
      var held = cart.items.reduce(function (sum, item) {
        return item.variant_id === device.variantId ? sum + item.quantity : sum;
      }, 0);
      return held + qty <= past.stock ? device : this.twinOf(device);
    },

    // The lead as its twin, for the add alone: a copy with what the add
    // reads swapped, as this.devices keeps the lead.
    twinOf: function (device) {
      return Object.assign({}, device, {
        variantId: device.past.variantId,
        code: device.past.code,
        deviceCode: device.past.deviceCode,
      });
    },

    addKitToCart: function (trigger) {
      var self = this;
      var t = this.totals();
      var device = t.device;
      var plan = t.plan;

      // Never a bare tracker: without the plan in the cart the kit has no
      // included year, and tracker-only is bought on the tracker's page.
      if (this.adding || !device.available || !device.variantId || !t.planUsable) return;

      // What "ct:kit_add_to_cart" reports: the kit as it is sent. The plan,
      // cadence and quantity controls stay live while the request is out.
      // Its device is settled with the tracker, once the cart is read.
      var sent = this.trackData();

      // Every tracker needs its own SIM/plan: the plan line matches the
      // tracker quantity.
      var qty = t.qty;
      var trackerItem = { id: device.variantId, quantity: qty };
      var planItem = { id: plan.variantId, quantity: qty, selling_plan: plan.sellingPlanId };

      // The plan line's visible "Billing" property, for the checkout's order
      // summary: only while the theme setting ct_offer_billing_line is
      // ticked (offer.billingLine, a boolean from the section), the offer
      // is on and the plan has a price. Otherwise the plan line carries
      // `_ct_kit`, `_ct_kit_label` and "For tracker" and nothing else. The
      // sentence is built in assets/ct-cart-lines.js (window.ctKitBilling)
      // from the offer's cycle count for this term and the plan's price in
      // cents — what its selling plan bills per cycle, as the section read
      // it. It is formatted there, not by money() here: the tracker page
      // hands over the same cents, so the two write one string. No helper
      // loaded → no "Billing".
      var monthly = t.billing !== 'annual';
      var billing = '';
      if (
        t.offerOn &&
        this.offer.billingLine === true &&
        t.planUnitCents > 0 &&
        typeof window.ctKitBilling === 'function'
      ) {
        billing = window.ctKitBilling({
          monthly: monthly,
          cycles: monthly ? this.offer.cyclesMonthly : this.offer.cyclesAnnual,
          cents: t.planUnitCents,
        });
      }

      // The plan line first, the tracker last: the cart lists the newest
      // line first, so the tracker ends up above its plan in the cart and
      // at checkout.
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = { items: [planItem, trackerItem] };
      if (cart) {
        body.sections = cart.getSectionsToRender().map(function (section) {
          return section.id;
        });
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      // Holds the buttons and the tracker cards until the whole sequence —
      // the cart read, the add, any undo and any resend — has settled.
      this.adding = true;
      if (this.errorEl) this.errorEl.hidden = true;
      this.updateCta(t);
      if (trigger) {
        trigger.classList.add('loading');
        trigger.setAttribute('aria-busy', 'true');
      }

      // The cart as the buyer left it, read first: the quantities an add that
      // fails is put back to (undoAdd below). A read that fails ends in the
      // catch underneath with no add sent, as ct-product.js's fitted add-on
      // path does — an add that could not be undone is never started.
      // The tracker's variant stays first in this list, whatever the order
      // of the request: undoAdd counts the first variant.
      var variantIds = [device.variantId, plan.variantId];
      var before = null;

      // Sends the kit as `chosen`, the one place `device` changes: from here
      // on it is that tracker, so everything below names what was really
      // sent. A copy where it is the twin: `t` and the receipt stay the
      // lead's. The twin's key is its code in lower case, as on its own card.
      var send = function (chosen) {
        device = chosen;
        if (device !== t.device) sent.device = device.code.toLowerCase();
        trackerItem.id = device.variantId;
        variantIds[0] = device.variantId;

        // Pair the two lines for the cart (device.deviceCode is "CT-V06" from
        // ct-device-detect; plan.key is e.g. "real-time-annual"; no fitted
        // add-on from here). No helper loaded → no properties.
        var kit =
          typeof window.ctKitProperties === 'function'
            ? window.ctKitProperties(device.deviceCode, plan.key, plan.title, '', billing)
            : null;
        if (kit) {
          trackerItem.properties = kit.tracker;
          planItem.properties = kit.plan;
        }

        return self.postJson(window.routes.cart_add_url, body);
      };

      this.readCart()
        .then(function (snapshot) {
          before = snapshot;
          return send(self.kitDevice(device, qty, before));
        })
        .then(function answered(response) {
          if (response.status) {
            // The cart refused the add — perhaps after taking part of it: an
            // over-stock add is answered with 422 once what stock allows has
            // gone in, which — the plan line being sent first — leaves plans
            // without their trackers. What went in comes back out, plans
            // and trackers alike, before the buyer is told. The message is
            // shown before the publish, so a subscriber that throws can't
            // replace it with the generic one.
            return self.undoAdd(before, variantIds).then(function (undo) {
              // Selling past stock (see the header): a kit refused as the
              // lead, with the cart back as it was, goes again as the twin.
              // Once: `device` is then no longer the lead, so a refusal of
              // the twin ends below, and nothing is shown for this one.
              if (device === t.device && device.past && undo.restored) {
                return send(self.twinOf(device)).then(answered);
              }
              self.showError(self.refusalMessage(response, undo, device.code, qty));
              safePublish(PUB_SUB_EVENTS.cartError, {
                source: 'ct-kit-builder',
                productVariantId: device.variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
            });
          }

          // The add HAS succeeded from here on. Nothing below may throw into
          // the catch underneath: that would show "Something went wrong"
          // over a kit that is in the cart and invite a second add. A
          // subscriber or notification error is logged, never surfaced.
          try {
            // "ct:kit_add_to_cart": the one point every confirmed add
            // passes — before the branch that leaves for the cart page, and
            // on neither path that undoes (the refusal above, the catch
            // underneath).
            self.track('kit_add_to_cart', sent);

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // The tracker's line, by its variant and never by its place in
            // the answer: the plan line was sent first. It is the line the
            // publish names and the one Dawn's notification is handed (what
            // shows if ct-cart-lines.js is not there to rebuild the lines).
            var added = Array.isArray(response.items) ? response.items : [];
            var trackerLine =
              added.filter(function (item) {
                return item.variant_id === device.variantId;
              })[0] ||
              added[0] ||
              response;
            safePublish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'ct-kit-builder',
              productVariantId: trackerLine.variant_id || device.variantId,
              cartData: response,
            });
            cart.renderContents(Object.assign({}, trackerLine, { sections: response.sections }));
            appendNotificationLines(cart, response);
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          } catch (error) {
            console.error(error);
          }
        })
        .catch(function (error) {
          console.error(error);
          var wrong = 'Something went wrong adding your kit to the cart. ';
          // The first cart read failed: nothing was sent.
          if (!before) {
            self.showError(wrong + 'Check your connection and try again.');
            return null;
          }
          // The add got no usable answer (no connection, a body that is not
          // JSON) — yet it may have gone in, and a retry would then double
          // the kit. So the cart is read again and whatever the two lines
          // grew by is taken back out, as ct-product.js does in its catch.
          // An add that never arrived finds nothing to take back and ends
          // as it always has; a cart that can't be read or put back is the
          // buyer's to check first.
          return self.undoAdd(before, variantIds).then(function (undo) {
            var advice = 'Check your connection and try again.';
            if (!undo.restored) advice = 'Check your cart and your connection before you try again.';
            else if (undo.taken) advice = 'Nothing was added: check your connection and try again.';
            self.showError(wrong + advice);
          });
        })
        .then(function () {
          if (trigger) {
            trigger.classList.remove('loading');
            trigger.removeAttribute('aria-busy');
          }
          self.adding = false;
          self.updateCta();
          // What was asked for while the add was in flight, in order. By now
          // the cart change has taken the fit answer for the tracker that
          // was added (ct-fit-details.js reads it as cartUpdate is
          // published), so the kit is free to change.
          var held = self.held.splice(0, self.held.length);
          held.forEach(function (run) {
            try {
              run();
            } catch (error) {
              console.error(error);
            }
          });
        });
    },

    // What the buyer is told when the cart refuses the add (`undo` is what
    // undoAdd resolved with). Shopify's own text stands only when nothing
    // went in: on a stock cap it reads "Only 15 items were added to your
    // cart", which the undo has just made untrue. The figure is the number
    // of trackers the add did get in: how many MORE the cart will take, not
    // the stock — a buyer who already holds some reads "more". With every
    // tracker in, the refusal was about something else (the plan line), for
    // a reason not known here: no figure, no cause named, and no advice a
    // lower quantity may not bear out.
    refusalMessage: function (response, undo, code, qty) {
      if (!undo.restored) {
        return 'Couldn’t add the kit, and part of it may still be in your cart. Check your cart before you try again.';
      }
      if (!undo.taken) return response.description || response.message || 'Couldn’t add the kit — try again.';
      var more = undo.added;
      if (more === 0) {
        return 'No more ' + code + ' trackers can be added right now, so nothing was added to your cart.';
      }
      if (more < qty) {
        return (
          'Only ' + more + (undo.had ? ' more ' : ' ') + code + plural(more, ' tracker', ' trackers') +
          ' can be added right now. Nothing was added to your cart: lower the quantity to ' + more +
          (more > 1 ? ' or fewer' : '') + ' and try again.'
        );
      }
      return 'Couldn’t add the whole kit, so nothing was added to your cart — try again.';
    },

    postJson: function (url, body) {
      var config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(body);
      return fetch(String(url), config).then(function (response) {
        return response.json();
      });
    },

    // The cart as it stands. An answer that is not a cart (an error body, no
    // `items`) rejects: read as an empty cart it would have undoAdd take a
    // kit from an earlier add out.
    readCart: function () {
      return fetch(String(window.routes.cart_url) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          if (!cart || cart.status || !Array.isArray(cart.items)) throw new Error('The cart could not be read.');
          return cart;
        });
    },

    // Takes back what a failed add put in — the rule of undoAdd in
    // assets/ct-product.js. For the cart lines of these variants, each slot
    // is brought back DOWN to its total quantity in `before` (0 for a slot
    // that was not there). Per slot total, never per line: a repeat of a kit
    // already in the cart merges into that kit's lines, and one slot can
    // span several lines (a discount split) — the excess comes off its last
    // line first. A slot at its old total is left alone, so a kit from an
    // earlier add survives untouched and an add that put nothing in changes
    // nothing. By line index, as there.
    // Three differences from ct-product.js:
    // - A slot is what Shopify merges an add into — the variant, the selling
    //   plan and the line item properties — not the line key. A key changes
    //   with the discounts on its line (Shopify: "not stable identifiers"),
    //   and an automatic discount can come and go with what is in the cart,
    //   which a failed add has just moved. By key, an earlier kit's tracker
    //   line whose discounts changed would look new and be taken out whole.
    // - Every slot of the snapshot must still be in the cart, at no less
    //   than its old total. If one is not, the cart was changed elsewhere
    //   meanwhile: nothing more is changed and the undo counts as failed.
    // - One change at a time, each worked out from the cart as the change
    //   before it left it (/cart/change answers with the whole cart), never
    //   from a list drawn up at the first read. A change can merge or split
    //   the lines of another slot (a discount that depends on the tracker
    //   count), which renumbers every line after them: the next number on a
    //   list would then be some other line — an unrelated product's. So:
    //   the last line in the cart of a slot still over its total is cut by
    //   what the slot is over; before the request that line is checked to
    //   be that slot at that quantity; after it the slots must be over by
    //   exactly that much less. Anything else stops the undo there, as
    //   failed. A line of any other variant is never named in a request.
    // Never rejects. Resolves with { restored, taken, added, had }: the cart
    // is back as the buyer left it; there was something to take back; how
    // many of the first variant (the tracker) the add had put in, null when
    // not known; how many of it the cart held before. The cart icon is
    // repainted and subscribers told (refreshCartState) unless the cart was
    // read and found as it was.
    // The tracker page's kit add uses the same rule — undoKitAdd in
    // assets/ct-product.js is this function's twin: change them together.
    undoAdd: function (before, variantIds) {
      var self = this;
      var result = { restored: false, taken: false, added: null, had: 0 };
      var slot = function (item) {
        var properties = item.properties || {};
        var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
        var pairs = Object.keys(properties)
          .sort()
          .map(function (name) {
            return [name, properties[name]];
          });
        return [item.variant_id, plan ? plan.id : '', JSON.stringify(pairs)].join('|');
      };
      var mine = function (item) {
        return variantIds.indexOf(item.variant_id) !== -1;
      };
      var count = function (cart, variantId) {
        return cart.items.reduce(function (sum, item) {
          return item.variant_id === variantId ? sum + item.quantity : sum;
        }, 0);
      };
      var wanted = {};
      before.items.filter(mine).forEach(function (item) {
        wanted[slot(item)] = (wanted[slot(item)] || 0) + item.quantity;
      });
      result.had = count(before, variantIds[0]);

      // Where `cart` stands against the snapshot: how far its slots are over
      // their old totals in all, and the next change — the last line in the
      // cart of a slot still over. Throws when a slot of the snapshot is
      // short.
      var survey = function (cart) {
        var slots = {};
        cart.items.forEach(function (item, index) {
          if (!mine(item)) return;
          var id = slot(item);
          if (!slots[id]) slots[id] = { total: 0, last: null };
          slots[id].total += item.quantity;
          slots[id].last = { line: index + 1, quantity: item.quantity };
        });
        Object.keys(wanted).forEach(function (id) {
          if (!slots[id] || slots[id].total < wanted[id]) {
            throw new Error('The cart lines no longer match the cart as it was read before the add.');
          }
        });
        var state = { over: 0, next: null };
        Object.keys(slots).forEach(function (id) {
          var over = slots[id].total - (wanted[id] || 0);
          var last = slots[id].last;
          if (over <= 0) return;
          state.over += over;
          if (!state.next || last.line > state.next.line) {
            state.next = { slot: id, line: last.line, from: last.quantity, cut: Math.min(last.quantity, over) };
          }
        });
        return state;
      };

      // One change, then again from the cart it answers with, until no slot
      // is over. Resolves false at the first thing that is not as expected.
      var settle = function (cart, state) {
        var next = state.next;
        if (!next) return true;
        result.taken = true;
        var item = cart.items[next.line - 1];
        if (!item || !mine(item) || slot(item) !== next.slot || item.quantity !== next.from) return false;
        // A change that would cut nothing can never bring the slot down.
        if (next.cut < 1) return false;
        return self
          .postJson(window.routes.cart_change_url, { line: next.line, quantity: next.from - next.cut })
          .then(function (changed) {
            if (!changed || changed.status || !Array.isArray(changed.items)) return false;
            var after = survey(changed);
            if (after.over !== state.over - next.cut) return false;
            return settle(changed, after);
          });
      };

      return this.readCart()
        .then(function (now) {
          var state = survey(now);
          result.added = Math.max(0, count(now, variantIds[0]) - result.had);
          return settle(now, state);
        })
        .then(function (ok) {
          result.restored = ok;
        })
        .catch(function (error) {
          console.error(error);
        })
        .then(function () {
          return result.restored && !result.taken ? null : self.refreshCartState();
        })
        .then(function () {
          return result;
        });
    },

    // After an undo: the header's cart icon from the Section Rendering API
    // (as Dawn's notification paints it), and a cartUpdate with the fresh
    // cart so any cart surface on the page re-reads. Never throws.
    refreshCartState: function () {
      return Promise.all([
        fetch(String(window.routes.cart_url) + '?sections=cart-icon-bubble').then(function (response) {
          return response.json();
        }),
        this.readCart(),
      ])
        .then(function (results) {
          var host = document.getElementById('cart-icon-bubble');
          var html = results[0] && results[0]['cart-icon-bubble'];
          if (host && html) {
            var source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
            host.innerHTML = source ? source.innerHTML : html;
          }
          safePublish(PUB_SUB_EVENTS.cartUpdate, { source: 'ct-kit-builder', cartData: results[1] });
        })
        .catch(function (error) {
          console.error(error);
        });
    },

    showError: function (message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      // An add from the bottom bar: bring the message into view.
      var rect = this.errorEl.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) this.showSummary();
    },

    /* ---------------------------------------------------------- analytics */

    // What both events carry: getState() without `fitted`.
    trackData: function () {
      var s = this.getState();
      return { set: s.set, device: s.device, plan: s.plan, billing: s.billing, qty: s.qty };
    },

    // Through window.ctTrack (assets/ct-base.js), which never throws —
    // looked up on each call, so the builder does not depend on the order
    // the two scripts load in.
    track: function (name, data) {
      if (typeof window.ctTrack === 'function') window.ctTrack(name, data);
    },

    /* ---------------------------------------------------------------- API */

    getState: function () {
      var s = this.state;
      var fitted = this.fitSelect && this.fitWrap && !this.fitWrap.hidden ? this.fitSelect.value : '';
      return { set: this.set, device: s.device, plan: s.plan, billing: s.billing, qty: s.qty, fitted: fitted };
    },

    setState: function (partial) {
      partial = partial || {};
      var self = this;
      // An add is in flight: the kit must not change under it (the click
      // guard in bind() says why). Held, and applied when it settles.
      if (this.adding) {
        this.held.push(function () {
          self.setState(partial);
        });
        return;
      }
      var s = this.state;
      if (partial.device && this.selectable(partial.device)) {
        s.device = String(partial.device).toLowerCase();
        s.deviceTouched = true;
      }
      if (partial.plan && this.data.plans[partial.plan]) {
        s.plan = partial.plan;
        this.planPicked();
      }
      if (partial.billing === 'monthly' || partial.billing === 'annual') s.billing = partial.billing;
      if (partial.qty != null) this.setQty(partial.qty);
      this.update();
      if (typeof partial.fitted === 'string' && this.fitSelect && this.fitWrap && !this.fitWrap.hidden) {
        // Shown, not saved: ct-fit-details.js saves on the buyer's own change.
        this.fitSelect.value = partial.fitted;
        if (this.fitSelect.selectedIndex < 0) this.fitSelect.value = '';
      }
    },

    refresh: function () {
      this.devices = this.readDevices();
      if (!this.devices.length) return;
      this.readControls();
      this.update(true);
    },

    // Select a tracker named elsewhere on the page and bring its card into
    // view — with the Vehicles / Assets switch above the builder too when
    // both fit, else the top of the builder, else the card alone.
    pick: function (key) {
      var self = this;
      key = String(key || '').toLowerCase();
      if (!this.selectable(key)) return false;
      // An add is in flight: the pick (selection, pulse and scroll) waits
      // for it to settle. Still "handled", so the link that asked for it
      // does not navigate away mid-request.
      if (this.adding) {
        this.held.push(function () {
          self.pick(key);
        });
        return true;
      }
      this.setState({ device: key });
      var device = this.deviceByKey(key);
      var card = device.card;
      if (device.input) device.input.focus({ preventScroll: true });

      card.classList.remove('trk-picked');
      void card.offsetWidth;
      card.classList.add('trk-picked');

      var top = headerHeight() + 16;
      var room = window.innerHeight - top - 16;
      var cardRect = card.getBoundingClientRect();
      var band = this.root.closest('.ct-section');
      var delta = null;
      [band ? band.querySelector('[data-trk-switch]') : null, this.root].forEach(function (anchor) {
        if (delta !== null || !anchor) return;
        var anchorTop = anchor.getBoundingClientRect().top;
        if (cardRect.bottom - anchorTop <= room) delta = anchorTop - top;
      });
      if (delta === null) delta = cardRect.top - top - Math.max(0, (room - cardRect.height) / 2);
      window.scrollTo({ top: Math.max(0, window.pageYOffset + delta), behavior: reduceMotion() ? 'auto' : 'smooth' });
      return true;
    },
  };

  /* -------------------------------------------------- mobile sticky bar */
  /* One bar at a time: the first builder that is on screen while its own
     "Add to cart" button is not. Also pins the desktop rail under the
     header (or by its bottom edge when it is taller than the window). */

  var barFrame = 0;

  function updateBars() {
    barFrame = 0;
    var shown = false;
    var viewport = window.innerHeight;
    var headerH = headerHeight();
    instances.forEach(function (instance) {
      if (!instance.ok) return;
      var rail = instance.root.querySelector('[data-kb-rail]');
      if (rail) {
        rail.style.top = Math.min(headerH + 16, viewport - rail.offsetHeight - 16) + 'px';
      }
      var bar = instance.root.querySelector('[data-kb-bar]');
      if (!bar) return;
      var show = false;
      if (!shown && window.innerWidth < DESKTOP) {
        var rect = instance.root.getBoundingClientRect();
        var main = instance.root.querySelector('[data-kb-add-main]');
        var mainRect = main ? main.getBoundingClientRect() : null;
        var onScreen = rect.top < viewport * 0.5 && rect.bottom > 160;
        var mainVisible = mainRect && mainRect.top < viewport && mainRect.bottom > 0;
        show = onScreen && !mainVisible;
      }
      if (bar.hidden === show) bar.hidden = !show;
      shown = shown || show;
    });
    document.documentElement.classList.toggle('ct-has-kb-bar', shown);
  }

  function scheduleBars() {
    if (barFrame) return;
    barFrame = window.requestAnimationFrame ? window.requestAnimationFrame(updateBars) : window.setTimeout(updateBars, 16);
  }

  window.addEventListener('scroll', scheduleBars, { passive: true });
  window.addEventListener('resize', scheduleBars);

  /* Chromium restores form state (Back without the back/forward cache, tab
     restore, duplicate tab) after this script has run and fires no change
     event; pageshow fires once that restore is done. */
  window.addEventListener('pageshow', function () {
    instances.forEach(function (instance) {
      instance.resync();
    });
  });

  /* A tracker picked somewhere else on the page (the type comparison's
     column buttons), by its code or an alias (deviceByAlias()). Handled
     only when a builder here can add it; otherwise the event is left alone
     and the link navigates. */
  document.addEventListener('ct:type-choose', function (event) {
    var detail = event.detail || {};
    var key = String(detail.device || '').toLowerCase();
    if (!key) return;
    for (var i = 0; i < instances.length; i += 1) {
      var device = instances[i].ok ? instances[i].deviceByAlias(key) : null;
      if (device && instances[i].selectable(device.key)) {
        event.preventDefault();
        instances[i].pick(device.key);
        return;
      }
    }
  });

  /* ---------------------------------------------------------------- init */

  function init(scope) {
    // Builders the theme editor has since re-rendered are dropped.
    for (var i = instances.length - 1; i >= 0; i -= 1) {
      if (!document.documentElement.contains(instances[i].root)) instances.splice(i, 1);
    }
    forEach.call((scope || document).querySelectorAll('[data-kit-builder]'), function (node) {
      if (node.ctKitBuilder) return;
      var instance = new KitBuilder(node);
      node.ctKitBuilder = instance;
      instances.push(instance);
    });
    scheduleBars();
  }

  window.CTKitBuilder = {
    init: init,
    instances: instances,
    get: function (el) {
      return el && el.ctKitBuilder && el.ctKitBuilder.ok ? el.ctKitBuilder : null;
    },
  };

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
